import { useSaleAssignDnd } from '@/pages/sale/components/sale-assign-dnd-provider';
import {
  clearSelectedAssign,
  getDragCustomerName,
  getSaleAssignDrag,
  getSaleOrderTableDroppableId,
  getSelectedAssignPayload,
  isSaleAssignPayload,
  subscribeSaleAssignDrag,
  subscribeSelectedAssign,
} from '@/pages/sale/lib/sale-comment-drag';
import { OrderResponse } from '@/types/order-type';
import { PinnedVariantWithStock } from '@/types/sale-type';
import { useDndMonitor, useDroppable } from '@dnd-kit/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DROP_SLOT_ROW_HEIGHT } from './pinned-board-constants';
import { createOptimisticOrder, resolveDropSlotFromPointer } from './pinned-board-utils';

type UseVariantAssignDropOptions = {
  variant: PinnedVariantWithStock;
  orderCount: number;
  orderRowsHeight: number;
  nextDropSlotNo: number;
  isCreatingOrderRef: React.MutableRefObject<boolean>;
  setOrderForVariants: React.Dispatch<React.SetStateAction<OrderResponse[]>>;
  setRemain: React.Dispatch<React.SetStateAction<number>>;
  onAssignToSlot?: (args: {
    payload: Record<string, unknown>;
    variant: PinnedVariantWithStock;
  }) => Promise<boolean>;
  onRefreshAfterAssign: () => Promise<void>;
};

export function useVariantAssignDrop({
  variant,
  orderCount,
  orderRowsHeight,
  nextDropSlotNo,
  isCreatingOrderRef,
  setOrderForVariants,
  setRemain,
  onAssignToSlot,
  onRefreshAfterAssign,
}: UseVariantAssignDropOptions) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const autoScrollToTargetRef = useRef(false);
  const autoScrollFrameRef = useRef<number | null>(null);

  const [isCreatingOrder, setIsCreatingOrder] = useState(false);

  const [isDragOverTable, setIsDragOverTable] = useState(false);
  const [isAssignDragActive, setIsAssignDragActive] = useState(false);
  const [dragPreviewName, setDragPreviewName] = useState<string | null>(null);
  const [targetDropSlotNo, setTargetDropSlotNo] = useState<number | null>(null);
  const [selectedPreviewName, setSelectedPreviewName] = useState<string | null>(null);

  useEffect(() => {
    isCreatingOrderRef.current = isCreatingOrder;
  }, [isCreatingOrder, isCreatingOrderRef]);

  const droppableId = getSaleOrderTableDroppableId(variant.id);
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({ id: droppableId });
  const { pointerClientY } = useSaleAssignDnd();

  const mergeScrollRef = useCallback(
    (node: HTMLDivElement | null) => {
      scrollContainerRef.current = node;
      setDroppableRef(node);
    },
    [setDroppableRef],
  );

  const resetDropHighlight = useCallback(() => {
    autoScrollToTargetRef.current = false;
    setIsDragOverTable(false);
    setTargetDropSlotNo(null);
    if (autoScrollFrameRef.current != null) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
  }, []);

  const resolveSlot = useCallback(
    (clientY: number) => {
      const container = scrollContainerRef.current;
      if (!container) return nextDropSlotNo;
      return resolveDropSlotFromPointer({
        clientY,
        container,
        orderCount,
        orderRowsHeight,
        nextDropSlotNo,
      });
    },
    [nextDropSlotNo, orderCount, orderRowsHeight],
  );

  const scrollToDropSlot = useCallback(
    (slotNo: number) => {
      const container = scrollContainerRef.current;
      if (!container || slotNo <= orderCount) return;

      const dropIndex = slotNo - orderCount - 1;
      const top = orderRowsHeight + dropIndex * DROP_SLOT_ROW_HEIGHT;
      const centered = top - container.clientHeight / 2 + DROP_SLOT_ROW_HEIGHT / 2;
      container.scrollTo({ top: Math.max(0, centered), behavior: 'smooth' });
    },
    [orderCount, orderRowsHeight],
  );

  const runEdgeAutoScroll = useCallback((clientY: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const edge = 40;
    let delta = 0;
    if (clientY - rect.top < edge) delta = -10;
    else if (rect.bottom - clientY < edge) delta = 10;
    if (delta === 0) return;

    if (autoScrollFrameRef.current != null) {
      cancelAnimationFrame(autoScrollFrameRef.current);
    }

    autoScrollFrameRef.current = requestAnimationFrame(() => {
      container.scrollTop += delta;
      autoScrollFrameRef.current = null;
    });
  }, []);

  useEffect(() => {
    const syncDragState = () => {
      const drag = getSaleAssignDrag();
      const selected = getSelectedAssignPayload();
      const dragging = !!drag;

      setIsAssignDragActive(dragging || !!selected);
      setDragPreviewName(dragging && drag ? getDragCustomerName(drag) : null);

      if (!dragging && !selected) {
        resetDropHighlight();
      }
    };

    const unsubDrag = subscribeSaleAssignDrag(syncDragState);
    const unsubSelect = subscribeSelectedAssign(() => {
      const selected = getSelectedAssignPayload();
      setIsAssignDragActive(!!getSaleAssignDrag() || !!selected);
      setSelectedPreviewName(selected ? getDragCustomerName(selected) : null);

      if (selected) {
        setTargetDropSlotNo(nextDropSlotNo);
      } else if (!getSaleAssignDrag()) {
        setTargetDropSlotNo(null);
      }
    });

    return () => {
      unsubDrag();
      unsubSelect();
    };
  }, [nextDropSlotNo, resetDropHighlight]);

  const assignPayloadToSlot = useCallback(
    async (payload: Record<string, unknown>) => {
      if (!onAssignToSlot || isCreatingOrderRef.current || !isSaleAssignPayload(payload)) {
        return;
      }

      const optimisticOrder = createOptimisticOrder(payload, variant.variant.price ?? 0);

      setIsCreatingOrder(true);
      setOrderForVariants((prev) => [...prev, optimisticOrder]);
      setRemain((prev) => Math.max(0, prev - 1));
      clearSelectedAssign();
      resetDropHighlight();

      try {
        const success = await onAssignToSlot({ payload, variant });
        if (success) {
          await onRefreshAfterAssign();
        } else {
          setOrderForVariants((prev) => prev.filter((order) => order.id !== optimisticOrder.id));
          setRemain((prev) => prev + 1);
        }
      } catch {
        setOrderForVariants((prev) => prev.filter((order) => order.id !== optimisticOrder.id));
        setRemain((prev) => prev + 1);
      } finally {
        setIsCreatingOrder(false);
      }
    },
    [
      isCreatingOrderRef,
      onAssignToSlot,
      onRefreshAfterAssign,
      resetDropHighlight,
      setIsCreatingOrder,
      setOrderForVariants,
      setRemain,
      variant,
    ],
  );

  useEffect(() => {
    if (!isOver || pointerClientY == null || isCreatingOrder) return;

    setIsDragOverTable(true);
    setTargetDropSlotNo(resolveSlot(pointerClientY));
    runEdgeAutoScroll(pointerClientY);

    if (!autoScrollToTargetRef.current) {
      autoScrollToTargetRef.current = true;
      scrollToDropSlot(nextDropSlotNo);
    }
  }, [
    isCreatingOrder,
    isOver,
    nextDropSlotNo,
    pointerClientY,
    resolveSlot,
    runEdgeAutoScroll,
    scrollToDropSlot,
  ]);

  useEffect(() => {
    if (isOver || getSaleAssignDrag() || getSelectedAssignPayload()) return;
    setIsDragOverTable(false);
    setTargetDropSlotNo(null);
  }, [isOver]);

  useDndMonitor({
    onDragEnd(event) {
      if (event.over?.id !== droppableId) return;

      const payload = event.active.data.current as Record<string, unknown> | undefined;
      if (!payload || !isSaleAssignPayload(payload)) return;

      void assignPayloadToSlot(payload);
    },
  });

  const handleTapAssign = useCallback(
    (_slotNo: number) => {
      const selected = getSelectedAssignPayload();
      if (!selected || isCreatingOrder) return;
      void assignPayloadToSlot(selected);
    },
    [assignPayloadToSlot, isCreatingOrder],
  );

  return {
    scrollContainerRef,
    mergeScrollRef,
    isCreatingOrder,
    isDragOverTable,
    isAssignDragActive,
    dragPreviewName: dragPreviewName ?? selectedPreviewName,
    selectedAssignActive: !!selectedPreviewName,
    targetDropSlotNo,
    handleTapAssign,
  };
}
